import {
  Box,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { AddItemIcon } from 'components/icons';
import {
  useCombobox,
  useMultipleSelection,
  UseMultipleSelectionStateChange,
} from 'downshift';
import { matchSorter } from 'match-sorter';
import React, { useRef, useState } from 'react';
import { Tag } from './Tag';
import { useLocalStorage } from 'hooks/useBrowserStorage';
import { useFeatureFlag } from 'utils/sessionProvider';

interface CreateTagInputProps {
  onUpdateTags: (tags: string[], onCompleted: () => void) => void;
  tags: string[];
  isUpdatingTags: boolean;
  skipAutoFocus?: boolean;
}

const RECENT_TAGS_LOCALSTORAGE_KEY = 'recent-tags';
const AMOUNT_OF_RECENT_TAGS = 10;
type RecentTagsStorage = {
  recentTags: string[];
};

function useRecentTags() {
  const [{ recentTags }, setRecentTags] = useLocalStorage<RecentTagsStorage>(
    RECENT_TAGS_LOCALSTORAGE_KEY,
    { recentTags: [] },
  );
  return {
    recentTags,
    addRecentTags: (newTags: string[]) => {
      setRecentTags({
        recentTags: Array.from(new Set([...newTags, ...recentTags])).slice(
          0,
          AMOUNT_OF_RECENT_TAGS,
        ),
      });
    },
  };
}

export const CreateTagInput = ({
  onUpdateTags,
  tags,
  isUpdatingTags,
  skipAutoFocus = false,
}: CreateTagInputProps) => {
  const isRenameTagsEnabled = useFeatureFlag('FEATURE_RENAME_TAGS_TO_LABELS');

  const { recentTags, addRecentTags } = useRecentTags();
  const [inputValue, setInputValue] = useState('');
  const suggestedTags = matchSorter(
    recentTags.filter((prevTag) => !tags.includes(prevTag)),
    inputValue || '',
  );
  const inputRef = useRef<HTMLInputElement>();

  const onAddTags = (newTags: string[]) => {
    const filteredNewTags = newTags.filter(
      (newTag) => tags.indexOf(newTag) === -1,
    );
    if (!filteredNewTags.length) {
      return;
    }

    onUpdateTags([...tags, ...filteredNewTags], () => {
      inputRef.current?.focus();
    });
    addRecentTags(newTags);
    setInputValue('');
    newTags.forEach((newTag) => removeSelectedItem(newTag));
  };

  const { getDropdownProps, addSelectedItem, removeSelectedItem } =
    useMultipleSelection({
      onStateChange: (state: UseMultipleSelectionStateChange<string>) => {
        if (!state || !state.selectedItems) {
          return;
        }
        onAddTags(state.selectedItems);
      },
    });

  const {
    isOpen,
    selectItem,
    getToggleButtonProps,
    getItemProps,
    getMenuProps,
    highlightedIndex,
    getComboboxProps,
    getInputProps,
  } = useCombobox<string | null>({
    inputValue,
    items: suggestedTags,
    stateReducer: (_, { changes, type }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: false,
          };
      }
      return changes;
    },
    onStateChange: ({ inputValue, type, selectedItem }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputChange:
          setInputValue(inputValue ? inputValue : '');
          break;
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          if (selectedItem) {
            addSelectedItem(selectedItem);
            setInputValue('');
            selectItem(null);
          }
      }
    },
  });

  return (
    <form
      onSubmit={(e) => {
        onAddTags([inputValue]);
        e.preventDefault();
      }}
      {...getComboboxProps()}
    >
      <InputGroup size="sm" {...getToggleButtonProps(getDropdownProps())}>
        <Input
          borderRadius="md"
          value={inputValue}
          placeholder={isRenameTagsEnabled ? t`Add a label` : t`Add a tag`}
          isDisabled={isUpdatingTags}
          autoFocus={!skipAutoFocus}
          {...getInputProps({
            ref: inputRef as any /* works, complains about legacy ref types but not important for ut*/,
            onChange: (event) => {
              setInputValue((event.target as HTMLInputElement).value);
            },
          })}
        />
        <InputRightElement>
          <IconButton
            type="submit"
            isDisabled={!inputValue}
            isLoading={isUpdatingTags}
            size="xs"
            variant="ghost"
            colorScheme="gray"
            icon={<AddItemIcon />}
            aria-label={isRenameTagsEnabled ? t`Create label` : t`Create tag`}
          />
        </InputRightElement>
      </InputGroup>

      <Box position="relative">
        <ul {...getMenuProps()} style={{ listStyle: 'none' }}>
          {!!suggestedTags.length && isOpen && (
            <SuggestedTagsPopover
              isOpen={true}
              suggestedTags={suggestedTags}
              highlightedIndex={highlightedIndex}
              getItemProps={getItemProps}
            />
          )}
        </ul>
      </Box>
    </form>
  );
};

interface SuggestedTagsProps {
  suggestedTags: string[];
  highlightedIndex: number;
  getItemProps: ({ item, index }: { item: string; index: number }) => any;
  isOpen: boolean;
}

const SuggestedTagsPopover = ({
  suggestedTags,
  highlightedIndex,
  getItemProps,
  isOpen,
}: SuggestedTagsProps) => {
  const isRenameTagsEnabled = useFeatureFlag('FEATURE_RENAME_TAGS_TO_LABELS');

  if (!isOpen) return null;

  return (
    <Box zIndex="popover" width="100%" position="absolute">
      <Stack
        spacing={1}
        p={2}
        top="7px"
        shadow="md"
        borderWidth="1px"
        borderRadius="md"
        maxHeight="sm"
        overflowY="scroll"
        bg="white"
        _dark={{ bg: 'gray.800' }}
      >
        <Heading fontSize="xs" opacity={0.6}>
          {isRenameTagsEnabled ? t`Recent labels` : t`Recent tags`}
        </Heading>
        {suggestedTags.length > 0 &&
          suggestedTags.map((item, index) => (
            <li key={item} {...getItemProps({ item, index })}>
              <Tag isActive={highlightedIndex === index}>{item}</Tag>
            </li>
          ))}
      </Stack>
    </Box>
  );
};
